import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import Papa from 'papaparse';
import { usePlatform } from '../context/PlatformContext';
import Modal from '../components/ui/Modal';
import StatusBadge from '../components/ui/StatusBadge';
import { parseExcelFile } from '../utils/excelParser';
import { generateCredentialId, generateVerificationId, extractInternId, ID_KEYS } from '../utils/idGenerator';
import { resolveTemplate, buildEmailData, DYNAMIC_VARIABLES, DEFAULT_INTERNSHIP_SUBJECT, DEFAULT_INTERNSHIP_BODY, DEFAULT_CERTIFICATE_SUBJECT, DEFAULT_CERTIFICATE_BODY } from '../utils/emailTemplates';
import {
  getBrevoStatus,
  sendSingleCandidateEmail,
  sendBulkCandidateEmails,
  sendTestEmail
} from '../utils/emailService';
import {
  FiMail, FiSend, FiRefreshCw, FiCheck, FiAlertCircle, FiChevronDown,
  FiChevronRight, FiShield, FiPlay, FiStopCircle, FiCheckCircle, FiX, FiClock,
  FiUploadCloud, FiFile, FiAlertTriangle, FiPlus, FiEye, FiTrash2
} from 'react-icons/fi';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_KEYS = ['name', 'full name', 'fullname', 'student name', 'candidate name', 'intern name', 'recipient'];
const EMAIL_KEYS = ['email', 'email address', 'e-mail', 'mail', 'emailid', 'email id'];

function findCol(headers, options) {
  return headers.find(h => options.includes(h.toLowerCase().trim())) || null;
}

export default function EmailCenter() {
  const {
    mode,
    candidates,
    addCandidates,
    deleteCandidate,
    deleteAllCandidates,
    settings,
    updateSettings,
    emailHistory,
    addEmailToHistory,
    updateCandidate
  } = usePlatform();

  const [activeTab, setActiveTab] = useState('compose'); // 'compose' | 'history' | 'settings'
  const [brevoStatus, setBrevoStatus] = useState({ connected: false, configured: false, loading: true });
  const [testEmailRecipient, setTestEmailRecipient] = useState('innovationhacksofficial@gmail.com');
  const [sendingTest, setSendingTest] = useState(false);

  // Custom Data Upload State
  const fileInputRef = useRef(null);
  const [previewRows, setPreviewRows] = useState(null);
  const [autoGenerateDocs, setAutoGenerateDocs] = useState(true);
  const [dragOver, setDragOver] = useState(false);

  // Single Compose Modal
  const [composeModalCandidate, setComposeModalCandidate] = useState(null);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeAttachPDF, setComposeAttachPDF] = useState(true);
  const [sendingSingle, setSendingSingle] = useState(false);

  // Bulk Send Modal & Queue State
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkPhase, setBulkPhase] = useState('confirm'); // 'confirm' | 'sending' | 'finished'
  const [bulkProgress, setBulkProgress] = useState({ total: 0, sent: 0, failed: 0, currentCandidate: null, results: [] });
  const cancellationRef = useRef({ isCancelled: false });

  // History detail accordion
  const [expandedEmailId, setExpandedEmailId] = useState(null);

  // Editable settings template state
  const [subjectTemplate, setSubjectTemplate] = useState(settings.emailSubject || '');
  const [bodyTemplate, setBodyTemplate] = useState(settings.emailBody || '');

  useEffect(() => {
    setSubjectTemplate(settings.emailSubject || '');
    setBodyTemplate(settings.emailBody || '');
  }, [settings, mode]);

  // Check Brevo Status on mount
  useEffect(() => {
    async function checkStatus() {
      const status = await getBrevoStatus();
      setBrevoStatus({ ...status, loading: false });
    }
    checkStatus();
  }, []);

  // Candidate Selection State (Individual & Select All)
  const [selectedIds, setSelectedIds] = useState(new Set());

  const toggleSelectAll = () => {
    if (selectedIds.size === generatedCandidates.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(generatedCandidates.map(c => c.id)));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) return;
    if (confirm(`Delete ${selectedIds.size} selected candidate(s)? This action cannot be undone.`)) {
      selectedIds.forEach(id => deleteCandidate(id));
      toast.success(`Deleted ${selectedIds.size} candidates`);
      setSelectedIds(new Set());
    }
  };

  const handleSendSelected = () => {
    if (selectedIds.size === 0) return;
    setShowBulkModal(true);
    setBulkPhase('confirm');
  };

  const generatedCandidates = candidates.filter(c => c.docStatus === 'generated');
  const pendingEmailCandidates = generatedCandidates.filter(c => c.emailStatus !== 'sent');
  const sentCandidates = generatedCandidates.filter(c => c.emailStatus === 'sent');
  const failedCandidates = generatedCandidates.filter(c => c.emailStatus === 'failed');

  // ── Custom Data Upload Handlers ────────────────────────────────
  const processUploadedRows = (rawRows) => {
    const headers = Object.keys(rawRows[0] || {});
    const nameKey = findCol(headers, NAME_KEYS);
    const emailKey = findCol(headers, EMAIL_KEYS);

    if (!nameKey || !emailKey) {
      toast.error(`Could not find Name or Email columns. Found: ${headers.join(', ')}`);
      return null;
    }

    const existingEmails = new Set(candidates.map(c => c.email?.toLowerCase()));
    return rawRows
      .filter(r => r[nameKey]?.trim())
      .map(r => ({
        ...r,
        _name: r[nameKey]?.trim(),
        _email: r[emailKey]?.trim(),
        _internId: extractInternId(r, headers),
        _emailValid: EMAIL_REGEX.test(r[emailKey]?.trim() || ''),
        _isDuplicate: existingEmails.has(r[emailKey]?.toLowerCase().trim()),
      }));
  };

  const handleCustomFileUpload = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: ({ data }) => {
          const rows = processUploadedRows(data);
          if (rows) setPreviewRows(rows);
        },
        error: (err) => {
          toast.error(`Failed to parse CSV: ${err.message}`);
        }
      });
    } else if (['xlsx', 'xls'].includes(ext)) {
      toast.loading('Reading Excel spreadsheet...', { id: 'xl-upload' });
      try {
        const data = await parseExcelFile(file);
        toast.dismiss('xl-upload');
        const rows = processUploadedRows(data);
        if (rows) setPreviewRows(rows);
      } catch (err) {
        toast.error(`Excel parse error: ${err.message}`, { id: 'xl-upload' });
      }
    } else {
      toast.error('Please upload a CSV (.csv) or Excel (.xlsx, .xls) file.');
    }
  };

  const confirmCustomImport = () => {
    if (!previewRows) return;
    const valid = previewRows.filter(r => !r._isDuplicate && r._emailValid);
    const dupes = previewRows.filter(r => r._isDuplicate).length;
    const invalid = previewRows.filter(r => !r._emailValid).length;

    const baseIdx = candidates.length;
    const newCandidates = valid.map((row, i) => {
      // CRITICAL: Always preserve the uploaded intern ID! Never overwrite with a generated cert ID.
      const uploadedId = row._internId || row.raw?._internId || null;
      const defaultPrefix = mode === 'certificate' ? (settings?.idPrefix || 'IH-CERT') : 'IH-FS';
      const fallbackGenId = autoGenerateDocs
        ? `${defaultPrefix}-${new Date().getFullYear()}-${String(baseIdx + i + 1).padStart(4, '0')}`
        : null;
      const genId = uploadedId || fallbackGenId;

      return {
        id: crypto.randomUUID(),
        name: row._name,
        email: row._email,
        college: row.College || row.college || row.Institution || row.University || '',
        role: row.Role || row.role || row.Position || row.Track || 'Full Stack Development Intern',
        department: row.Department || row.department || row.Branch || 'Technology',
        duration: row.Duration || row.duration || '1 Month',
        startDate: row.StartDate || row['Start Date'] || '2026-08-22',
        endDate: row.EndDate || row['End Date'] || '2026-09-22',
        docStatus: genId ? 'generated' : 'pending',
        emailStatus: 'pending',
        certStatus: 'active',
        generatedId: genId,
        verificationId: genId || generateVerificationId(mode === 'internship' ? 'INT' : 'CERT'),
        createdAt: new Date().toISOString(),
        mode,
        expiryDate: new Date(Date.now() + (settings?.expiryDays || 365) * 86400000).toISOString(),
        raw: row,
      };
    });

    addCandidates(newCandidates);
    const msgs = [`✅ ${newCandidates.length} imported into Email Center`];
    if (dupes) msgs.push(`⚠️ ${dupes} duplicates skipped`);
    if (invalid) msgs.push(`❌ ${invalid} invalid emails skipped`);
    toast.success(msgs.join(' · '));
    setPreviewRows(null);
  };

  const openCompose = (candidate) => {
    const data = buildEmailData(candidate, settings, mode);
    const subjectTpl = settings.emailSubject ||
      (mode === 'internship' ? DEFAULT_INTERNSHIP_SUBJECT : DEFAULT_CERTIFICATE_SUBJECT);
    const bodyTpl = settings.emailBody ||
      (mode === 'internship' ? DEFAULT_INTERNSHIP_BODY : DEFAULT_CERTIFICATE_BODY);
    setComposeSubject(resolveTemplate(subjectTpl, data));
    setComposeBody(resolveTemplate(bodyTpl, data));
    setComposeModalCandidate(candidate);
  };

  // Send Single Email
  const handleSendSingle = async () => {
    if (!composeModalCandidate) return;
    setSendingSingle(true);
    const toastId = toast.loading(`Sending email to ${composeModalCandidate.name} via Brevo...`);

    const activeTemplateUrl = mode === 'certificate' ? '/Certificate.jpeg' : '/template.png';

    try {
      const result = await sendSingleCandidateEmail({
        candidate: composeModalCandidate,
        settings,
        mode,
        templateUrl: activeTemplateUrl,
        customSubject: composeSubject,
        customBody: composeBody,
        attachPDF: composeAttachPDF,
      });

      toast.success(`✅ Email sent to ${composeModalCandidate.name}!`, { id: toastId });
      updateCandidate(composeModalCandidate.id, {
        emailStatus: 'sent',
        brevoMessageId: result.messageId,
        lastEmailSentAt: result.sentAt,
        lastError: null,
      });
      addEmailToHistory({
        id: result.messageId || crypto.randomUUID(),
        candidateId: composeModalCandidate.id,
        candidateName: composeModalCandidate.name,
        to: composeModalCandidate.email,
        subject: composeSubject,
        body: composeBody,
        attachPDF: composeAttachPDF,
        status: 'sent',
        sentAt: result.sentAt,
        mode,
      });
      setComposeModalCandidate(null);
    } catch (error) {
      const errMsg = error.message || 'Failed to send email';
      toast.error(`❌ Send failed: ${errMsg}`, { id: toastId });
      updateCandidate(composeModalCandidate.id, {
        emailStatus: 'failed',
        lastError: errMsg,
      });
      addEmailToHistory({
        id: crypto.randomUUID(),
        candidateId: composeModalCandidate.id,
        candidateName: composeModalCandidate.name,
        to: composeModalCandidate.email,
        subject: composeSubject,
        body: composeBody,
        attachPDF: composeAttachPDF,
        status: 'failed',
        errorMessage: errMsg,
        sentAt: new Date().toISOString(),
        mode,
      });
    } finally {
      setSendingSingle(false);
    }
  };

  // Retry an email from history or candidate row
  const handleRetryCandidate = async (candidate) => {
    const toastId = toast.loading(`Retrying email to ${candidate.name} via Brevo...`);
    try {
      const result = await sendSingleCandidateEmail({
        candidate,
        settings,
        mode,
        templateUrl: '/template.png',
        attachPDF: true,
      });

      toast.success(`✅ Successfully resent to ${candidate.name}!`, { id: toastId });
      updateCandidate(candidate.id, {
        emailStatus: 'sent',
        brevoMessageId: result.messageId,
        lastEmailSentAt: result.sentAt,
        lastError: null,
      });
      addEmailToHistory({
        id: result.messageId || crypto.randomUUID(),
        candidateId: candidate.id,
        candidateName: candidate.name,
        to: candidate.email,
        subject: result.subject,
        body: result.body,
        attachPDF: true,
        status: 'sent',
        isRetry: true,
        sentAt: result.sentAt,
        mode,
      });
    } catch (error) {
      const errMsg = error.message || 'Retry failed';
      toast.error(`❌ Retry failed: ${errMsg}`, { id: toastId });
      updateCandidate(candidate.id, {
        emailStatus: 'failed',
        lastError: errMsg,
      });
    }
  };

  // Start Bulk Dispatch
  const startBulkDispatch = async (targetCandidates = pendingEmailCandidates) => {
    if (targetCandidates.length === 0) {
      return toast.error('No pending candidates ready to send.');
    }

    setBulkPhase('sending');
    cancellationRef.current.isCancelled = false;

    // Track how many results we have already applied to updateCandidate.
    // onProgress fires both when a candidate STARTS sending (results unchanged)
    // and when it FINISHES (results grows by 1). By tracking the index we
    // processed up to, we apply each result exactly once — using the confirmed
    // Brevo provider response as the source of truth.
    let lastProcessedIdx = 0;

    const applyNewResults = (results) => {
      for (let i = lastProcessedIdx; i < results.length; i++) {
        const r = results[i];
        // Only update status after the provider has confirmed: status is 'sent'
        // (Brevo 201 OK) or 'failed' (network/API error). Never 'pending'.
        updateCandidate(r.candidateId, {
          emailStatus: r.status,                // 'sent' | 'failed'
          brevoMessageId: r.messageId || null,
          lastEmailSentAt: r.sentAt,
          lastError: r.errorMessage || null,
        });
        addEmailToHistory({
          id: r.messageId || crypto.randomUUID(),
          candidateId: r.candidateId,
          candidateName: r.candidateName,
          to: r.recipient,
          subject: r.subject,
          body: r.body,
          attachPDF: true,
          status: r.status,
          errorMessage: r.errorMessage,
          sentAt: r.sentAt,
          mode,
        });
      }
      lastProcessedIdx = results.length;
    };

    const activeTemplateUrl = mode === 'certificate' ? '/Certificate.jpeg' : '/template.png';

    const res = await sendBulkCandidateEmails({
      candidates: targetCandidates,
      settings,
      mode,
      templateUrl: activeTemplateUrl,
      cancellationToken: cancellationRef.current,
      onProgress: (prog) => {
        setBulkProgress(prog);
        // Apply any results that arrived since the last callback, including
        // the definitive final pass (prog.isFinal === true).
        applyNewResults(prog.results);
      },
    });

    setBulkPhase('finished');
    toast.success(`Batch completed: ${res.sent} sent, ${res.failed} failed.`);
  };


  // Send Test Email
  const handleTestEmail = async () => {
    if (!testEmailRecipient || !testEmailRecipient.includes('@')) {
      return toast.error('Please enter a valid recipient email.');
    }

    setSendingTest(true);
    const toastId = toast.loading(`Sending test email to ${testEmailRecipient}...`);
    try {
      const result = await sendTestEmail(testEmailRecipient, 'Admin');
      toast.success(`✅ Test email delivered! Message ID: ${result.messageId?.substring(0, 18)}...`, { id: toastId });
    } catch (error) {
      toast.error(`❌ Test failed: ${error.message}`, { id: toastId });
    } finally {
      setSendingTest(false);
    }
  };

  // Save Template Settings
  const handleSaveTemplates = () => {
    updateSettings({
      emailSubject: subjectTemplate,
      emailBody: bodyTemplate,
    });
    toast.success('Email templates saved successfully!');
  };

  const tabs = [
    { id: 'compose', label: 'Compose & Send' },
    { id: 'history', label: `History (${emailHistory.length})` },
    { id: 'settings', label: 'Brevo API & Templates' },
  ];

  return (
    <div className="space-y-6 pb-12">
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h1 className="page-title">Email Center</h1>
          <p className="page-subtitle">Upload custom data · Brevo REST API · Candidate Email Dispatch & Tracking</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 border border-emerald-100 rounded-xl text-xs font-medium text-emerald-800">
            <FiShield className="text-emerald-600 w-4 h-4" />
            <span>Sender: <strong className="font-black">Innovation Hacks</strong> &lt;innovationhacksofficial@gmail.com&gt;</span>
          </div>
        </div>
      </div>

      {/* ── Brevo Connection Banner ─────────────────────────────── */}
      <div className={`p-4 rounded-xl border flex items-center justify-between flex-wrap gap-3 ${brevoStatus.configured ? 'bg-brand-50/50 border-brand-100 text-brand-900' : 'bg-amber-50 border-amber-100 text-amber-900'}`}>
        <div className="flex items-center gap-3 text-xs">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${brevoStatus.configured ? 'bg-sky-100 text-sky-600' : 'bg-amber-100 text-amber-600'}`}>
            <FiMail className="w-4 h-4" />
          </div>
          <div>
            <div className="font-bold text-sm">
              {brevoStatus.configured ? 'Brevo Transactional REST API Ready' : 'Brevo API Key Setup Required'}
            </div>
            <div className="text-slate-500 text-[11px] mt-0.5">
              {brevoStatus.configured
                ? 'Server environment is active. Emails are dispatched directly via Brevo REST API.'
                : 'Please configure BREVO_API_KEY in server environment (.env) to enable live email delivery.'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              toast.loading('Checking Brevo connection...', { id: 'status' });
              const s = await getBrevoStatus();
              setBrevoStatus(s);
              if (s.connected || s.configured) toast.success('Brevo service is active and responsive.', { id: 'status' });
              else toast.error('Brevo API key not found in .env', { id: 'status' });
            }}
            className="px-3.5 py-1.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-bold hover:bg-slate-50 transition-all flex items-center gap-1.5">
            <FiRefreshCw className="w-3 h-3" /> Check Status
          </button>
        </div>
      </div>

      {/* ── Navigation Tabs ──────────────────────────────────────── */}
      <div className="flex gap-1.5 bg-surface-100 p-1.5 rounded-xl w-fit">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all ${activeTab === t.id ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: COMPOSE & SEND ─────────────────────────────────── */}
      {activeTab === 'compose' && (
        <div className="space-y-5">
          {/* Top Banner Card with UPLOAD CUSTOM DATA + SEND ALL Actions */}
          <div className="glass-card rounded-2xl p-6 flex flex-wrap gap-4 items-center justify-between bg-gradient-to-r from-white to-sky-50/30">
            <div>
              <div className="text-base font-semibold text-navy-900">🚀 Bulk Transactional Email Dispatch</div>
              <div className="text-xs text-slate-800 font-semibold mt-1 flex items-center gap-2">
                <span className="font-bold text-slate-700">{pendingEmailCandidates.length} pending</span>
                <span>•</span>
                <span className="font-bold text-emerald-600">{sentCandidates.length} sent</span>
                {failedCandidates.length > 0 && (
                  <>
                    <span>•</span>
                    <span className="font-bold text-rose-600">{failedCandidates.length} failed</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Hidden file input for custom dataset */}
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                ref={fileInputRef}
                onChange={e => handleCustomFileUpload(e.target.files[0])}
              />

              {/* 📥 Upload Custom Data Button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-5 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold text-xs hover:border-sky-400 hover:text-sky-600 transition-all shadow-sm">
                <FiUploadCloud className="w-4 h-4 text-sky-600" />
                Upload Custom Dataset (CSV / Excel)
              </button>

              {/* Retry Failed Button */}
              {failedCandidates.length > 0 && (
                <button
                  onClick={() => {
                    setShowBulkModal(true);
                    setBulkPhase('confirm');
                  }}
                  className="flex items-center gap-2 px-5 py-3 bg-rose-600 text-white rounded-xl font-bold text-xs hover:bg-rose-500 transition-all shadow-md shadow-rose-500/20">
                  <FiRefreshCw className="w-3.5 h-3.5" /> Retry Failed ({failedCandidates.length})
                </button>
              )}

              {/* Send All Pending Button */}
              <button
                onClick={() => {
                  setShowBulkModal(true);
                  setBulkPhase('confirm');
                }}
                disabled={pendingEmailCandidates.length === 0}
                className="btn-primary px-6 py-3 text-xs disabled:opacity-50 shadow-md">
                <FiSend className="w-4 h-4" /> SEND ALL PENDING ({pendingEmailCandidates.length})
              </button>
            </div>
          </div>

          {/* ── Selection Action Floating Bar ──────────────────────── */}
          {selectedIds.size > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-brand-50 border border-brand-200 rounded-2xl flex flex-wrap items-center justify-between gap-4 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-xl bg-brand-600 text-white font-bold text-xs flex items-center justify-center shadow-xs">
                  {selectedIds.size}
                </span>
                <span className="font-bold text-slate-900 text-sm">
                  {selectedIds.size} candidate{selectedIds.size > 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSendSelected}
                  className="btn-primary text-xs py-2 px-4 shadow-sm"
                >
                  <FiSend className="w-3.5 h-3.5" /> Send Emails to Selected ({selectedIds.size})
                </button>
                <button
                  onClick={handleDeleteSelected}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 active:scale-95 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5"
                >
                  <FiTrash2 className="w-3.5 h-3.5" /> Delete Selected ({selectedIds.size})
                </button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="px-3.5 py-2 bg-white text-slate-700 border border-slate-200 hover:bg-slate-100 rounded-xl text-xs font-bold transition-all"
                >
                  Deselect All
                </button>
              </div>
            </motion.div>
          )}

          {/* Candidates Email Table or Empty Dropzone */}
          <div className="glass-card rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-surface-200 font-bold text-slate-950 text-base flex justify-between items-center flex-wrap gap-3 bg-white">
              <div className="flex items-center gap-3">
                <span>Candidate Email List</span>
                <span className="text-xs font-semibold px-2.5 py-1 bg-surface-100 text-slate-700 rounded-lg border border-surface-200">
                  {generatedCandidates.length} Candidates Ready
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-secondary text-xs py-2 px-3.5"
                >
                  <FiPlus className="w-3.5 h-3.5" /> Import Custom Rows
                </button>
                {candidates.length > 0 && (
                  <button
                    onClick={() => {
                      if (confirm(`Are you sure you want to delete all ${candidates.length} candidate record(s)? This action cannot be undone.`)) {
                        deleteAllCandidates();
                        toast.success('All candidate records deleted.');
                      }
                    }}
                    className="px-3.5 py-2 bg-red-50 text-red-700 hover:bg-red-600 hover:text-white border border-red-200 rounded-xl text-xs font-bold transition-all shadow-xs flex items-center gap-1.5"
                  >
                    <FiTrash2 className="w-3.5 h-3.5" /> Delete All ({candidates.length})
                  </button>
                )}
              </div>
            </div>

            {generatedCandidates.length === 0 ? (
              <div
                className={`p-12 text-center border-2 border-dashed m-6 rounded-2xl transition-all cursor-pointer ${dragOver ? 'border-brand-500 bg-brand-50/50' : 'border-surface-200 hover:border-brand-400 hover:bg-surface-50'}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => {
                  e.preventDefault();
                  setDragOver(false);
                  handleCustomFileUpload(e.dataTransfer.files[0]);
                }}
                onClick={() => fileInputRef.current?.click()}>
                <div className="w-14 h-14 rounded-2xl bg-brand-50 flex items-center justify-center text-brand-600 mx-auto mb-3">
                  <FiUploadCloud className="w-7 h-7" />
                </div>
                <div className="font-bold text-slate-950 text-base">Upload Custom Data to Send Emails</div>
                <p className="text-xs text-slate-600 font-medium mt-1 max-w-sm mx-auto">
                  Drag and drop your CSV or Excel dataset with Name & Email columns, or click to browse.
                </p>
                <button
                  type="button"
                  className="mt-4 btn-primary text-xs py-2.5 px-6">
                  Select CSV / Excel File
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto p-4 bg-surface-50/50">
                <table className="w-full text-left border-separate border-spacing-y-3.5">
                  <thead>
                    <tr>
                      {/* Checkbox Column */}
                      <th className="px-5 py-3 w-14 text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.size > 0 && selectedIds.size === generatedCandidates.length}
                          ref={el => {
                            if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < generatedCandidates.length;
                          }}
                          onChange={toggleSelectAll}
                          className="w-4.5 h-4.5 rounded text-brand-600 focus:ring-brand-500 border-surface-300 cursor-pointer accent-brand-600"
                          title="Select / Deselect All"
                        />
                      </th>
                      <th className="px-4 py-3 text-center font-black text-slate-500 uppercase tracking-wider text-[11px] w-12">
                        #
                      </th>
                      <th className="px-5 py-3 text-left font-black text-slate-950 uppercase tracking-wider text-[11px] min-w-[240px]">
                        Candidate
                      </th>
                      <th className="px-5 py-3 text-left font-black text-slate-950 uppercase tracking-wider text-[11px] min-w-[220px]">
                        Email
                      </th>
                      <th className="px-5 py-3 text-left font-black text-slate-950 uppercase tracking-wider text-[11px] min-w-[180px]">
                        Credential ID
                      </th>
                      <th className="px-5 py-3 text-left font-black text-slate-950 uppercase tracking-wider text-[11px] min-w-[140px]">
                        Email Status
                      </th>
                      <th className="px-6 py-3 text-right font-black text-slate-950 uppercase tracking-wider text-[11px] min-w-[190px]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {generatedCandidates.map((c, i) => {
                      const isSelected = selectedIds.has(c.id);
                      return (
                        <tr
                          key={c.id}
                          className={`group transition-all duration-200 ${
                            isSelected
                              ? 'bg-brand-50/60 shadow-sm ring-1 ring-brand-300'
                              : 'bg-white hover:bg-surface-50 hover:shadow-card'
                          }`}
                        >
                          {/* Row Checkbox */}
                          <td className="px-5 py-5 text-center rounded-l-2xl border-y border-l border-surface-200/90 bg-inherit">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelectOne(c.id)}
                              className="w-4.5 h-4.5 rounded text-brand-600 focus:ring-brand-500 border-surface-300 cursor-pointer accent-brand-600"
                            />
                          </td>

                          {/* Index */}
                          <td className="px-4 py-5 text-xs font-bold text-slate-400 text-center border-y border-surface-200/90 bg-inherit">
                            {i + 1}
                          </td>

                          {/* Candidate info with CIRCULAR AVATAR */}
                          <td className="px-5 py-5 border-y border-surface-200/90 bg-inherit">
                            <div className="flex items-center gap-4">
                              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand-600 via-brand-700 to-navy-900 flex items-center justify-center text-white text-xs font-black flex-shrink-0 shadow-sm ring-2 ring-brand-100">
                                {c.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className="font-bold text-slate-950 text-sm tracking-tight leading-snug">{c.name}</div>
                                {c.college && (
                                  <div className="text-xs font-semibold text-slate-500 mt-1 truncate max-w-[220px]">{c.college}</div>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Email */}
                          <td className="px-5 py-5 border-y border-surface-200/90 bg-inherit">
                            <span className="text-slate-900 font-bold font-mono text-xs block truncate max-w-[220px]">
                              {c.email}
                            </span>
                          </td>

                          {/* Credential ID */}
                          <td className="px-5 py-5 border-y border-surface-200/90 bg-inherit">
                            <code className="text-xs font-black text-brand-700 bg-brand-50 px-3 py-1.5 rounded-xl border border-brand-200 inline-block font-mono tracking-tight shadow-2xs">
                              {c.generatedId || c.verificationId}
                            </code>
                          </td>

                          {/* Status */}
                          <td className="px-5 py-5 border-y border-surface-200/90 bg-inherit">
                            <StatusBadge status={c.emailStatus || 'pending'} />
                          </td>

                          {/* Actions */}
                          <td className="px-6 py-5 rounded-r-2xl border-y border-r border-surface-200/90 bg-inherit text-right">
                            <div className="flex items-center justify-end gap-3">
                              {c.emailStatus === 'failed' ? (
                                <button
                                  onClick={() => handleRetryCandidate(c)}
                                  className="h-10 px-5 bg-rose-600 hover:bg-rose-700 text-white rounded-tl-2xl rounded-br-2xl rounded-tr-lg rounded-bl-lg text-xs font-bold transition-all shadow-xs flex items-center justify-center gap-2 whitespace-nowrap min-w-[125px]">
                                  <FiRefreshCw className="w-3.5 h-3.5" /> Retry
                                </button>
                              ) : (
                                <button
                                  onClick={() => openCompose(c)}
                                  className={`h-10 px-5 rounded-tl-2xl rounded-br-2xl rounded-tr-lg rounded-bl-lg text-xs font-bold transition-all shadow-xs flex items-center justify-center gap-2 whitespace-nowrap min-w-[125px] active:scale-[0.98] ${
                                    c.emailStatus === 'sent'
                                      ? 'bg-surface-100 text-slate-800 hover:bg-surface-200 border border-surface-300'
                                      : 'bg-brand-600 hover:bg-brand-700 text-white shadow-sm shadow-brand-600/20'
                                  }`}>
                                  <FiMail className="w-3.5 h-3.5" /> {c.emailStatus === 'sent' ? 'Resend' : 'Send Mail'}
                                </button>
                              )}

                              {/* Prominent Default Red Delete Button */}
                              <button
                                onClick={() => {
                                  if (confirm(`Delete candidate "${c.name}"?`)) {
                                    deleteCandidate(c.id);
                                    toast.success(`Deleted ${c.name}`);
                                  }
                                }}
                                title="Delete candidate"
                                className="h-10 w-10 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white border border-red-200 rounded-xl transition-all shadow-2xs flex items-center justify-center flex-shrink-0"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB 2: HISTORY ────────────────────────────────────────── */}
      {activeTab === 'history' && (
        <div className="glass-card rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-surface-100 font-semibold text-navy-900 text-sm flex justify-between items-center">
            <span>Email Dispatch Logs</span>
            <span className="text-xs text-slate-800 font-semibold font-normal">{emailHistory.length} total records</span>
          </div>

          {emailHistory.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 mx-auto mb-3">
                <FiClock className="w-6 h-6" />
              </div>
              <div className="font-bold text-slate-700 text-sm">No email history yet</div>
              <p className="text-xs text-slate-800 font-semibold mt-1">Dispatched emails will appear here with Brevo Message IDs and timestamps.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {emailHistory.map(e => (
                <div key={e.id} className="p-5 hover:bg-slate-50/40 transition-colors">
                  <div
                    className="flex items-start gap-4 cursor-pointer"
                    onClick={() => setExpandedEmailId(expandedEmailId === e.id ? null : e.id)}>
                    <StatusBadge status={e.status} />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900 text-sm truncate">{e.subject}</div>
                      <div className="text-xs text-slate-800 font-semibold mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>To: <strong className="text-slate-600">{e.to}</strong></span>
                        {e.candidateName && <span>({e.candidateName})</span>}
                        <span>•</span>
                        <span>{e.sentAt ? new Date(e.sentAt).toLocaleString() : 'Just now'}</span>
                        {e.isRetry && <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-[9px] font-black uppercase">Retry</span>}
                      </div>
                    </div>

                    {e.status === 'failed' && (
                      <button
                        onClick={(ev) => {
                          ev.stopPropagation();
                          const cand = candidates.find(c => c.id === e.candidateId);
                          if (cand) handleRetryCandidate(cand);
                        }}
                        className="flex items-center gap-1.5 px-3 py-1 bg-rose-50 text-rose-600 border border-rose-100 rounded-xl text-xs font-bold hover:bg-rose-100 transition-all">
                        <FiRefreshCw className="w-3 h-3" /> Retry
                      </button>
                    )}

                    {expandedEmailId === e.id ? (
                      <FiChevronDown className="text-slate-400 w-4 h-4 flex-shrink-0 mt-1" />
                    ) : (
                      <FiChevronRight className="text-slate-400 w-4 h-4 flex-shrink-0 mt-1" />
                    )}
                  </div>

                  {/* Expanded log details */}
                  {expandedEmailId === e.id && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-4 ml-14 p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3 text-xs">
                      {e.errorMessage && (
                        <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl font-mono text-[11px]">
                          <strong>Failure Reason:</strong> {e.errorMessage}
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-3 text-slate-600">
                        <div><strong className="text-slate-800">Brevo Message ID:</strong> <code className="font-mono text-sky-700">{e.id || 'N/A'}</code></div>
                        <div><strong className="text-slate-800">Attachment:</strong> {e.attachPDF ? '✓ Personalized PDF Attached' : 'None'}</div>
                      </div>

                      <div>
                        <div className="font-bold text-slate-700 mb-1">Body Content:</div>
                        <pre className="text-slate-600 bg-white p-3 rounded-xl border border-slate-200 whitespace-pre-wrap font-sans text-xs max-h-48 overflow-y-auto">
                          {e.body}
                        </pre>
                      </div>
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB 3: BREVO SETTINGS & TEMPLATES ──────────────────────── */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Brevo Test Connection Tool */}
          <div className="glass-card rounded-2xl p-8 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-sky-50 flex items-center justify-center text-sky-600">
                <FiShield className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-navy-900">Brevo API Status & Test Email</h2>
                <p className="text-xs text-slate-900 font-bold">Verify that your server environment is configured properly.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-2 text-xs">
                <div><span className="text-slate-900 font-bold uppercase tracking-wider text-[10px]">Verified Sender Name:</span> <div className="font-semibold text-navy-900 mt-0.5">Innovation Hacks</div></div>
                <div><span className="text-slate-900 font-bold uppercase tracking-wider text-[10px]">Verified Sender Email:</span> <div className="font-semibold text-brand-600 font-mono mt-0.5">innovationhacksofficial@gmail.com</div></div>
                <div><span className="text-slate-900 font-bold uppercase tracking-wider text-[10px]">Server Storage:</span> <div className="font-medium text-slate-600 mt-0.5">Loaded from server <code className="font-mono bg-slate-200 px-1 py-0.5 rounded">.env</code> (Secure)</div></div>
              </div>

              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3">
                <div className="section-title">Send Live Test Email</div>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="Enter test recipient email"
                    value={testEmailRecipient}
                    onChange={e => setTestEmailRecipient(e.target.value)}
                    className="flex-1 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono focus:outline-none focus:border-sky-400"
                  />
                  <button
                    onClick={handleTestEmail}
                    disabled={sendingTest}
                    className="px-4 py-2 bg-sky-600 text-white rounded-xl text-xs font-bold hover:bg-sky-500 transition-all flex items-center gap-1.5 disabled:opacity-50">
                    {sendingTest ? <FiRefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FiSend className="w-3.5 h-3.5" />}
                    Send Test
                  </button>
                </div>
                <div className="text-[11px] text-slate-700 font-semibold">Sends a real transactional email via Brevo REST API to verify delivery.</div>
              </div>
            </div>
          </div>

          {/* Email Template Customization */}
          <div className="glass-card rounded-2xl p-8 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-navy-900 capitalize">{mode} Email Template</h2>
                <p className="text-xs text-slate-900 font-bold">Customize the default email subject and message sent to candidates.</p>
              </div>
              <button
                onClick={handleSaveTemplates}
                className="px-6 py-2.5 bg-slate-900 text-white rounded-xl font-bold text-xs hover:bg-slate-800 transition-all shadow-sm">
                Save Template
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block section-title mb-2">Subject Template</label>
                <input
                  value={subjectTemplate}
                  onChange={e => setSubjectTemplate(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-400 font-medium"
                />
              </div>

              <div>
                <label className="block section-title mb-2">Message Body Template</label>
                <textarea
                  value={bodyTemplate}
                  onChange={e => setBodyTemplate(e.target.value)}
                  rows={9}
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-sky-400 resize-none font-mono leading-relaxed"
                />
              </div>

              <div>
                <div className="section-title mb-2">Available Dynamic Variables</div>
                <div className="flex flex-wrap gap-2">
                  {DYNAMIC_VARIABLES.map(v => (
                    <button
                      key={v.key}
                      onClick={() => {
                        setBodyTemplate(prev => prev + ' ' + v.key);
                        toast.success(`Inserted ${v.key}`);
                      }}
                      type="button"
                      className="px-2.5 py-1 bg-sky-50 text-sky-700 hover:bg-sky-100 border border-sky-100 rounded-lg text-[10px] font-bold transition-colors">
                      {v.key} <span className="text-sky-400 font-normal">({v.label})</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Custom Data Import Preview Modal ────────────────────── */}
      <AnimatePresence>
        {previewRows && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setPreviewRows(null)} />
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="relative bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between px-7 py-5 border-b border-surface-100 bg-slate-50/60">
                <div>
                  <h2 className="font-semibold text-navy-900 text-lg">Custom Dataset Import Preview</h2>
                  <p className="text-xs text-slate-900 font-bold">Import recipients directly to Email Center</p>
                </div>
                <button onClick={() => setPreviewRows(null)} className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700">
                  <FiX className="w-4 h-4" />
                </button>
              </div>

              <div className="p-7 space-y-5">
                {/* Summary badges */}
                <div className="flex flex-wrap gap-3">
                  <span className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-xl text-xs font-black">
                    <FiCheck className="w-3.5 h-3.5" /> {previewRows.filter(r => !r._isDuplicate && r._emailValid).length} Valid
                  </span>
                  <span className="flex items-center gap-1.5 px-3.5 py-2 bg-amber-50 text-amber-700 border border-amber-100 rounded-xl text-xs font-black">
                    <FiAlertTriangle className="w-3.5 h-3.5" /> {previewRows.filter(r => r._isDuplicate).length} Duplicates
                  </span>
                  <span className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-50 text-rose-700 border border-rose-100 rounded-xl text-xs font-black">
                    <FiAlertCircle className="w-3.5 h-3.5" /> {previewRows.filter(r => !r._emailValid).length} Invalid Email
                  </span>
                </div>

                {/* Auto generate docs toggle */}
                <div className="flex items-center gap-3 p-3.5 bg-sky-50/60 border border-sky-100 rounded-2xl">
                  <input
                    type="checkbox"
                    id="auto-gen-docs"
                    checked={autoGenerateDocs}
                    onChange={e => setAutoGenerateDocs(e.target.checked)}
                    className="w-4 h-4 accent-sky-600 rounded cursor-pointer"
                  />
                  <label htmlFor="auto-gen-docs" className="text-xs font-bold text-slate-700 cursor-pointer">
                    Automatically generate unique credential IDs & mark ready for document PDF attachment
                  </label>
                </div>

                {/* Rows table */}
                <div className="overflow-auto max-h-60 rounded-2xl border border-slate-100">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left font-black text-slate-400 uppercase tracking-widest">Name</th>
                        <th className="px-4 py-3 text-left font-black text-slate-400 uppercase tracking-widest">Email</th>
                        <th className="px-4 py-3 text-left font-black text-slate-400 uppercase tracking-widest">Details</th>
                        <th className="px-4 py-3 text-left font-black text-slate-400 uppercase tracking-widest">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {previewRows.map((r, i) => (
                        <tr key={i} className={r._isDuplicate ? 'bg-amber-50/40' : !r._emailValid ? 'bg-rose-50/40' : ''}>
                          <td className="px-4 py-2.5 font-bold text-slate-800">{r._name}</td>
                          <td className="px-4 py-2.5 text-slate-500 font-mono">{r._email}</td>
                          <td className="px-4 py-2.5 text-slate-400">{r.College || r.Department || r.Role || '—'}</td>
                          <td className="px-4 py-2.5">
                            {r._isDuplicate
                              ? <span className="text-amber-600 font-black flex items-center gap-1"><FiAlertTriangle className="w-3 h-3"/>Duplicate</span>
                              : !r._emailValid
                              ? <span className="text-rose-600 font-black flex items-center gap-1"><FiAlertCircle className="w-3 h-3"/>Bad Email</span>
                              : <span className="text-emerald-600 font-black flex items-center gap-1"><FiCheck className="w-3 h-3"/>Ready</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end gap-3 pt-1">
                  <button onClick={() => setPreviewRows(null)} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-200">
                    Cancel
                  </button>
                  <button onClick={confirmCustomImport}
                    className="px-8 py-2.5 bg-sky-600 text-white rounded-xl font-bold text-sm hover:bg-sky-500 transition-all shadow-lg shadow-sky-500/20">
                    Import {previewRows.filter(r => !r._isDuplicate && r._emailValid).length} Recipients
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Single Compose Modal ──────────────────────────────────── */}
      <Modal
        isOpen={!!composeModalCandidate}
        onClose={() => setComposeModalCandidate(null)}
        title={`Send Email — ${composeModalCandidate?.name}`}
        size="md">
        {composeModalCandidate && (
          <div className="p-6 space-y-4">
            <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100 text-xs flex justify-between items-center">
              <div>
                <span className="font-black text-slate-400 uppercase tracking-wider text-[10px]">Recipient: </span>
                <span className="font-bold text-slate-800">{composeModalCandidate.email}</span>
              </div>
              <code className="font-mono text-sky-600 bg-sky-50 px-2 py-0.5 rounded border border-sky-100 text-xs">
                {composeModalCandidate.generatedId || composeModalCandidate.verificationId}
              </code>
            </div>

            <div>
              <label className="block section-title mb-2">Subject</label>
              <input
                value={composeSubject}
                onChange={e => setComposeSubject(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-400"
              />
            </div>

            <div>
              <label className="block section-title mb-2">Body</label>
              <textarea
                value={composeBody}
                onChange={e => setComposeBody(e.target.value)}
                rows={8}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-sky-400 resize-none font-mono"
              />
            </div>

            <div className="flex items-center gap-3 p-3 bg-sky-50/50 border border-sky-100 rounded-xl">
              <input
                type="checkbox"
                id="pdf-attach-compose"
                checked={composeAttachPDF}
                onChange={e => setComposeAttachPDF(e.target.checked)}
                className="w-4 h-4 accent-sky-600 cursor-pointer"
              />
              <label htmlFor="pdf-attach-compose" className="text-xs font-bold text-slate-700 cursor-pointer">
                Attach Personalized PDF document ({mode === 'internship' ? 'Offer Letter' : 'Certificate'})
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setComposeModalCandidate(null)}
                disabled={sendingSingle}
                className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-200">
                Cancel
              </button>
              <button
                onClick={handleSendSingle}
                disabled={sendingSingle}
                className="flex items-center gap-2 px-8 py-2.5 bg-sky-600 text-white rounded-xl font-bold text-sm hover:bg-sky-500 transition-all shadow-lg shadow-sky-500/20 disabled:opacity-50">
                {sendingSingle ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSend className="w-4 h-4" />}
                Send via Brevo
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Bulk Send Modal ───────────────────────────────────────── */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={bulkPhase === 'sending' ? undefined : () => setShowBulkModal(false)} />
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="relative bg-white rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-7 py-5 border-b border-surface-100 bg-slate-50/60">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-sky-600 flex items-center justify-center text-white">
                  <FiSend className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-semibold text-navy-900 text-base">Bulk Email Dispatch</h2>
                  <p className="text-xs text-slate-900 font-bold">Brevo Transactional REST API</p>
                </div>
              </div>
              {bulkPhase !== 'sending' && (
                <button onClick={() => setShowBulkModal(false)} className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700">
                  <FiX className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="p-7 space-y-5">
              {bulkPhase === 'confirm' && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600 font-medium">
                    Ready to dispatch personalized emails with PDF attachments to <span className="font-semibold text-navy-900">{pendingEmailCandidates.length}</span> candidate(s).
                  </p>
                  <div className="p-4 bg-sky-50 border border-sky-100 rounded-2xl text-xs text-sky-800 flex items-center gap-2">
                    <FiShield className="w-4 h-4 text-sky-600 flex-shrink-0" />
                    <span>Sender: <strong>Innovation Hacks</strong> &lt;innovationhacksofficial@gmail.com&gt;</span>
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <button onClick={() => setShowBulkModal(false)} className="px-6 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-bold text-sm">Cancel</button>
                    <button
                      onClick={() => startBulkDispatch(pendingEmailCandidates)}
                      className="px-8 py-2.5 bg-sky-600 text-white rounded-xl font-black text-sm hover:bg-sky-500 shadow-lg shadow-sky-500/20">
                      Confirm & Start Dispatch
                    </button>
                  </div>
                </div>
              )}

              {bulkPhase === 'sending' && (
                <div className="space-y-5">
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-3 bg-slate-100 rounded-xl">
                      <div className="text-[10px] font-black text-slate-400 uppercase">Total</div>
                      <div className="text-xl font-semibold text-navy-900">{bulkProgress.total}</div>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                      <div className="text-[10px] font-black text-emerald-600 uppercase">Sent</div>
                      <div className="text-xl font-black text-emerald-700">{bulkProgress.sent}</div>
                    </div>
                    <div className="p-3 bg-rose-50 rounded-xl border border-rose-100">
                      <div className="text-[10px] font-black text-rose-600 uppercase">Failed</div>
                      <div className="text-xl font-black text-rose-700">{bulkProgress.failed}</div>
                    </div>
                  </div>

                  {bulkProgress.currentCandidate && (
                    <div className="p-4 bg-sky-50 border border-sky-100 rounded-2xl flex items-center gap-3">
                      <FiRefreshCw className="w-5 h-5 text-sky-600 animate-spin flex-shrink-0" />
                      <div className="min-w-0 text-xs">
                        <div className="font-semibold text-brand-900 truncate">Sending to: {bulkProgress.currentCandidate.name}</div>
                        <div className="text-sky-600 font-mono">{bulkProgress.currentCandidate.email}</div>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      onClick={() => { cancellationRef.current.isCancelled = true; toast('Stopping queue...'); }}
                      className="px-6 py-2 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold hover:bg-rose-100">
                      Stop Queue
                    </button>
                  </div>
                </div>
              )}

              {bulkPhase === 'finished' && (
                <div className="space-y-4 text-center py-2">
                  <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-2">
                    <FiCheck className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-navy-900">Dispatch Complete</h3>
                  <p className="text-xs text-slate-500">{bulkProgress.sent} sent successfully · {bulkProgress.failed} failed</p>
                  <div className="flex justify-end gap-3 pt-2">
                    <button onClick={() => setShowBulkModal(false)} className="px-8 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-bold">Done</button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
